/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * SUPERVISOR TO REGIME (v1_396).
 *
 * v1_395 let a firm choose which law its files are reasoned in, but the choice
 * lived in a settings screen the MLRO had to find. A Swiss tenant who never
 * opened it got a file argued in UK statutory instruments and nothing said so.
 * Silent defaults are the failure this codebase keeps paying for.
 *
 * The firm's own supervisor is captured when the tenant is created, which is
 * the moment someone already knows the answer, and the regime follows from it.
 * That takes the setting off the MLRO entirely: more work for you once, at
 * setup, and none for them ever.
 *
 * WHERE THE SUPERVISOR IS UNKNOWN the regime is not guessed. It stays at the
 * default and the board says it is running on the default rather than on the
 * firm's own regime, so an officer can see that the citations are comparative.
 */

/** Ordered [pattern, regime]. First match wins. */
const SUPERVISOR_PATTERNS = [
  // Mauritius (v1_622). "fsc" on its own is deliberately NOT matched: Gibraltar,
  // the BVI, the Isle of Man and others also have an FSC, and mapping any of
  // them to Mauritian law would be the silent-default failure again.
  [/\b(fsc\s*\(?\s*mauritius|financial\s+services\s+commission\s+(of\s+)?mauritius|bank\s+of\s+mauritius|fiu\s+mauritius|mauritius|mauritian)\b/i, 'MU'],
  // Canada (v1_622). Listed before the EU set because "AMF" alone is the
  // French supervisor pattern and AMF Québec must not land in the EU regime.
  [/\b(fintrac|amf\s+qu[eé]bec|autorit[eé]\s+des\s+march[eé]s\s+financiers|canada|canadian|canrev)\b/i, 'CA'],

  // EU member states and their supervisors. One AMLD citation set serves all of
  // them, because every member state transposes the same directive.
  [/\b(bank\s+of\s+lithuania|lietuvos\s+bankas|lithuania|lithuanian|fntt)\b/i, 'EU'],
  [/\b(mfsa|fiau|malta|maltese)\b/i, 'EU'],
  [/\b(cysec|cbc|cyprus|cypriot)\b/i, 'EU'],
  [/\b(cssf|luxembourg|luxembourgish)\b/i, 'EU'],
  [/\b(bafin|germany|german)\b/i, 'EU'],
  [/\b(dnb|afm|netherlands|dutch)\b/i, 'EU'],
  [/\b(acpr|amf|france|french)\b/i, 'EU'],
  [/\b(banca\s+d\W?italia|consob|oam|italy|italian)\b/i, 'EU'],
  [/\b(banco\s+de\s+espa|cnmv|sepblac|spain|spanish)\b/i, 'EU'],
  [/\b(finantsinspektsioon|estonia|estonian|rab\b)\b/i, 'EU'],
  [/\b(latvijas\s+banka|latvia|latvian)\b/i, 'EU'],
  [/\b(finanstilsynet|denmark|danish|norway|norwegian)\b/i, 'EU'],
  [/\b(finansinspektionen|sweden|swedish)\b/i, 'EU'],
  [/\b(fin-?fsa|finland|finnish)\b/i, 'EU'],
  [/\b(fma|austria|austrian)\b/i, 'EU'],
  [/\b(nbb|fsma|belgium|belgian)\b/i, 'EU'],
  [/\b(knf|poland|polish)\b/i, 'EU'],
  [/\b(cnb|czech|czechia|slovakia|slovak)\b/i, 'EU'],
  [/\b(banco\s+de\s+portugal|cmvm|portugal|portuguese)\b/i, 'EU'],
  [/\b(bank\s+of\s+greece|greece|greek)\b/i, 'EU'],
  [/\b(mnb|hungary|hungarian|romania|romanian|bulgaria|bulgarian|croatia|croatian|slovenia|slovenian)\b/i, 'EU'],
  [/\b(european\s+union|amld|amla|eu\s+member\s+state)\b/i, 'EU'],

  // Gibraltar (v1_978). Before the United Kingdom, whose pattern reads
  // "british": Gibraltar is a British Overseas Territory with its own AML law
  // and its own supervisor, so a Gibraltar firm cites Gibraltar law (part B,
  // src/shared/regimes.js GI), never UK law.
  [/\b(gfsc|gibraltar\s+financial\s+services\s+commission|gibraltar|gibraltarian)\b/i, 'GI'],

  // Switzerland
  [/\b(vqf|finma|sro|self[- ]regulat\w*\s+organisation|polyreg|arif|osfin|swiss)\b/i, 'CH'],
  // Ireland
  [/\b(central\s+bank\s+of\s+ireland|cbi\b|ireland|irish)\b/i, 'IE'],
  // United Kingdom
  [/\b(fca|financial\s+conduct\s+authority|hmrc|prudential\s+regulation|pra\b|gambling\s+commission|united\s+kingdom|uk\b|british|england|scotland|wales)\b/i, 'UK']
];

/** Supervisors offered at tenant setup, with the regime each implies. */
export const KNOWN_SUPERVISORS = [
  { label: 'FCA (United Kingdom)', value: 'FCA', regime: 'UK' },
  { label: 'HMRC (United Kingdom)', value: 'HMRC', regime: 'UK' },
  { label: 'VQF (Switzerland)', value: 'VQF', regime: 'CH' },
  { label: 'FINMA (Switzerland)', value: 'FINMA', regime: 'CH' },
  { label: 'Other Swiss SRO', value: 'SRO_CH', regime: 'CH' },
  { label: 'Bank of Lithuania', value: 'BOL', regime: 'EU' },
  { label: 'MFSA (Malta)', value: 'MFSA', regime: 'EU' },
  { label: 'CySEC (Cyprus)', value: 'CYSEC', regime: 'EU' },
  { label: 'CSSF (Luxembourg)', value: 'CSSF', regime: 'EU' },
  { label: 'BaFin (Germany)', value: 'BAFIN', regime: 'EU' },
  { label: 'Other EU supervisor', value: 'EU_OTHER', regime: 'EU' },
  { label: 'Central Bank of Ireland', value: 'CBI', regime: 'IE' },
  { label: 'FSC (Mauritius)', value: 'FSC_MU', regime: 'MU' },
  { label: 'Bank of Mauritius', value: 'BOM', regime: 'MU' },
  { label: 'FINTRAC (Canada)', value: 'FINTRAC', regime: 'CA' },
  { label: 'AMF Québec (Canada)', value: 'AMF_QC', regime: 'CA' },
  { label: 'GFSC (Gibraltar)', value: 'GFSC', regime: 'GI' },
  { label: 'Other or not yet known', value: 'OTHER', regime: null }
];

/**
 * The regimes the platform can cite accurately. A supervisor outside these is
 * recorded as the firm's own, but its regime stays unset rather than being
 * mapped to the nearest available law. Showing a Singaporean firm MLR 2017 and
 * calling it their regime would be worse than showing them nothing.
 */
export const CITEABLE_REGIMES = [
  { id: 'UK', label: 'United Kingdom', statute: 'MLR 2017' },
  { id: 'EU', label: 'European Union', statute: 'AMLD' },
  { id: 'CH', label: 'Switzerland', statute: 'GwG / AMLA' },
  { id: 'IE', label: 'Ireland', statute: 'CJA 2010' },
  { id: 'MU', label: 'Mauritius', statute: 'FIAMLA 2002 / FIAML Regulations 2018' },
  { id: 'CA', label: 'Canada', statute: 'PCMLTFA / PCMLTFR' },
  { id: 'GI', label: 'Gibraltar', statute: 'Proceeds of Crime Act 2015 (Gibraltar)' }
];

/**
 * Derive the regime from a supervisor name.
 * Returns { regime, derivedFrom } or { regime: null } when it cannot be read,
 * which is reported rather than guessed.
 */
export function regimeFromSupervisor(supervisor) {
  const raw = String(supervisor || '').trim();
  if (!raw) return { regime: null, derivedFrom: null };

  const known = KNOWN_SUPERVISORS.find(k => k.value.toLowerCase() === raw.toLowerCase());
  if (known) return { regime: known.regime, derivedFrom: known.label };

  for (const [pattern, regime] of SUPERVISOR_PATTERNS) {
    if (pattern.test(raw)) return { regime, derivedFrom: raw };
  }
  return { regime: null, derivedFrom: null };
}
